import { h } from '@omni-torn/ui';
import { backfillCursor, ownEntryTime, pageSpan } from '@omni-torn/shared';
import { ownHistoryOn, ownRead, ownSend } from '../own-history';
import { readLogs, summariseLogs, type LogEntry } from '../read';
import { server, torn } from '../session';
import { box, kids, loading, num, problem, short, table, tct } from '../ui';

/** Most pages of 100 one visit may read (each page is one Torn call). */
const MAX_PAGES = 20;

/** "energy_used" → "energy used: 25" (the data Torn attaches to a log line). */
function details(d: Record<string, unknown>): string {
  return Object.entries(d)
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
    .slice(0, 6)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'number' ? v.toLocaleString() : String(v).replace(/<[^>]*>/g, '').slice(0, 60)}`)
    .join(' · ');
}

/** A Torn read of your log sent to the server (terms 19), with the stretch it covered: one upload per read. */
async function sendPage(body: unknown, cursor: number | null): Promise<void> {
  if (!ownHistoryOn()) return;
  const raw = body && typeof body === 'object' && (body as { log?: unknown }).log && typeof (body as { log?: unknown }).log === 'object' ? (body as { log: Record<string, unknown> }).log : {};
  const times = Object.values(raw).map((e) => ownEntryTime('log', e)).filter((t): t is number => t !== null);
  await ownSend('log', raw, pageSpan(cursor, times, 100, Date.now()));
}

export async function renderLogs(view: HTMLElement): Promise<void> {
  const onServer = ownHistoryOn();
  const body = h('div', {}, loading('Reading your log…'));
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Logs'), h('p', { class: 'muted' }, onServer ? 'Your activity log (Full Access key), kept for you on Omni-Torn until you delete your data — whatever any of your devices read, and older pages read while you’re away. Only you can see it.' : 'Your activity log (Full Access key). With background tracking on, Omni-Torn keeps the last 90 days for you — only you can see it.')),
    body,
  );
  let logs: LogEntry[] = [];
  let pages = 0;
  let done = false;
  /** Background tracking's copy still has older entries to give. */
  let keptMore = true;
  let keptCount = 0;
  let category = '';
  let search = '';

  const add = (got: LogEntry[]): number => {
    const seen = new Set(logs.map((l) => l.id));
    const fresh = got.filter((l) => !seen.has(l.id));
    logs = [...logs, ...fresh].sort((a, b) => b.at - a.at);
    return fresh.length;
  };
  /** Older entries: from what background tracking kept first (no Torn call), then from Torn. */
  const more = async (): Promise<void> => {
    const oldest = logs[logs.length - 1];
    if (keptMore) {
      const kept = await server<{ log: Record<string, unknown>; count: number }>('GET', `/api/me/logs?limit=500${oldest ? `&before=${oldest.at}` : ''}`).catch(() => null);
      keptCount = kept?.count ?? keptCount;
      const got = readLogs({ log: kept?.log ?? {} });
      if (got.length && add(got)) return;
      keptMore = false;
    }
    // The server holds your whole log (terms 19): nothing older to ask Torn for.
    if (onServer && backfillCursor((await ownRead('log', { limit: 1 }).catch(() => null))?.spans ?? []) === 0) {
      done = true;
      return;
    }
    const params: Record<string, string> = { selections: 'log' };
    if (oldest) params.to = String(Math.floor(oldest.at / 1000) - 1);
    const page = await torn('/user/', params, 600_000);
    await sendPage(page, oldest ? Math.floor(oldest.at / 1000) * 1000 : null);
    const got = readLogs(page);
    pages++;
    if (!add(got) || pages >= MAX_PAGES) done = true;
  };

  try {
    // Torn's newest page and the kept copy's newest, together.
    const [live, kept] = await Promise.all([torn('/user/', { selections: 'log' }, 60_000), server<{ log: Record<string, unknown>; count: number }>('GET', '/api/me/logs?limit=500').catch(() => null)]);
    add(readLogs(live));
    void sendPage(live, null);
    keptCount = kept?.count ?? 0;
    if (!kept || !add(readLogs({ log: kept.log })) || Object.keys(kept.log).length < 500) keptMore = false;
    pages = 1;
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }

  const summary = h('div', {});
  const list = h('div', {});
  const moreBtn = h('button', { type: 'button', class: 'btn-small' }, 'Load older');
  const moreStatus = h('span', { class: 'muted' });
  const catSelect = h('select', { 'aria-label': 'Category' }) as HTMLSelectElement;
  const searchBox = h('input', { type: 'search', placeholder: 'Search the log…', class: 'filter', 'aria-label': 'Search the log' }) as HTMLInputElement;

  const draw = () => {
    const sum = summariseLogs(logs);
    const cats = sum.byCategory.map((c) => c.category);
    catSelect.replaceChildren(h('option', { value: '' }, 'All categories'), ...cats.map((c) => h('option', { value: c, selected: c === category }, c)));
    const top = sum.byCategory[0]?.count ?? 1;
    summary.replaceChildren(
      h(
        'div',
        { class: 'tiles' },
        tile('Entries read', num(logs.length)),
        ...(keptCount ? [tile(onServer ? 'Kept for you on Omni-Torn' : 'Kept for you (90 days)', num(keptCount))] : []),
        tile('Covering', sum.from && sum.to ? `${tct(sum.from)} → ${tct(sum.to)}` : '—'),
        tile('Energy in the gym', num(sum.energyGym)),
        tile('Xanax', num(sum.xanax)),
        ...Object.entries(sum.gains).map(([k, v]) => tile(`${k[0]!.toUpperCase()}${k.slice(1)} gained`, short(v))),
      ),
      h(
        'div',
        { class: 'cards two' },
        box(
          'By category',
          h(
            'ul',
            { class: 'bars-list' },
            ...sum.byCategory.slice(0, 14).map((c) => {
              const fill = h('span', { class: 'bl-fill' });
              fill.style.width = `${(c.count / top) * 100}%`;
              const li = h('li', { class: 'clickable', title: 'Show only these' }, h('span', { class: 'bl-label' }, c.category), h('span', { class: 'bl-track' }, fill), h('span', { class: 'bl-n' }, num(c.count)));
              li.addEventListener('click', () => ((category = c.category), draw()));
              return li;
            }),
          ),
        ),
        box('Most common', table(sum.byTitle.slice(0, 15), [
          { label: 'What', get: (t) => t.title, sort: (t) => t.title },
          { label: 'Category', get: (t) => h('span', { class: 'muted' }, t.category), sort: (t) => t.category },
          { label: 'Times', get: (t) => num(t.count), sort: (t) => t.count, cls: 'r' },
        ], { initial: 2 })),
      ),
    );
    const shown = logs.filter((l) => (!category || l.category === category) && (!search || `${l.title} ${l.category} ${details(l.data)}`.toLowerCase().includes(search)));
    list.replaceChildren(
      ...kids(
      table(shown.slice(0, 500), [
        { label: 'When', get: (l) => h('span', { title: new Date(l.at).toUTCString() }, tct(l.at)), sort: (l) => l.at },
        { label: 'What', get: (l) => l.title, sort: (l) => l.title },
        { label: 'Category', get: (l) => h('span', { class: 'muted' }, l.category), sort: (l) => l.category },
        { label: 'Details', get: (l) => h('span', { class: 'muted small' }, details(l.data)) },
      ], { initial: 0, empty: 'Nothing matches.' }),
      shown.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${shown.length}.`) : null,
      ),
    );
    moreBtn.disabled = done;
    moreStatus.textContent = done ? (pages >= MAX_PAGES ? ` That’s ${MAX_PAGES * 100} — enough for one visit.` : ' That’s everything Torn has.') : '';
  };
  catSelect.addEventListener('change', () => ((category = catSelect.value), draw()));
  searchBox.addEventListener('input', () => ((search = searchBox.value.trim().toLowerCase()), draw()));
  moreBtn.addEventListener('click', async () => {
    moreBtn.disabled = true;
    moreStatus.textContent = ' Reading…';
    try {
      await more();
    } catch (err) {
      moreStatus.textContent = ` ${(err as Error).message}`;
    }
    draw();
  });

  body.replaceChildren(summary, box('Log', [h('div', { class: 'toolbar' }, catSelect, searchBox, moreBtn, moreStatus), list]));
  draw();
}

function tile(label: string, value: string): HTMLElement {
  return h('div', { class: 'tile' }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value' }, value));
}
